import moment from "moment";

import { type TBTStatusBadgeDisplayTypes } from "@btnet/core-components";

import { type BuilderInfo, CurrencyLocale, type TimeLocale } from "helpers/AppProvider.types";

import { type BTSelectItem } from "types/apiResponse/apiResponse";
import { type ILanguageConstants } from "types/constants";
import { ProposalGroupTypes } from "types/enum";

import {
    formatCustomFieldToString,
    mapResponseToCustomFieldDefinition,
    normalizeCustomFieldFormValueForDisplay,
} from "utilities/customFields/customFields";
import { formatDateAsApFormatWithTime } from "utilities/date/date";
import { isTaxGroupIdValid } from "utilities/tax/tax.utilities";

import { AddressFieldTypes } from "commonComponents/entity/address/Address/Address.api.types";
import { type ICustomFieldFormValues } from "commonComponents/entity/customField/CustomFieldContainer/CustomFieldContainer";
import {
    type ICustomFieldOptionsList,
    type ICustomFieldResponse,
} from "commonComponents/entity/customField/CustomFieldContainer/CustomFieldContainer.types";
import { formatDate } from "commonComponents/utilities/DateDisplay/DateDisplay";
import { type IDateDisplayFormattingProps } from "commonComponents/utilities/DateDisplay/DateDisplayDate.utilities";
import { getFormatLineItemsFromFormatItemList } from "commonComponents/utilities/ProposalBaseLineItemContainer/WorksheetLineItemContainer.utility";
import { type StatusTypes } from "commonComponents/utilities/Status/StatusDisplay";

import {
    type ApprovalDetailsForDisplay,
    type BTFileSystem,
    MediaTypes,
    type ProposalColumnTypes,
    type ProposalForDisplay,
    type ProposalForDisplayCustomerAddress,
    type ProposalForDisplayFormatItemsItem,
    type ProposalLineItemForDisplay,
    type ProposalPrintOptionsDto,
    ProposalSignatureRequirement,
    ProposalStatus,
} from "handlers";

import {
    type ChangeOrderEntity,
    type IChangeOrderFormValues,
} from "entity/changeOrder/ChangeOrder/ChangeOrder.api.types";
import { type IChangeOrderWorksheetFormValues } from "entity/changeOrder/common/changeOrder.utilities";
import { type WorksheetLineItem } from "entity/estimate/common/estimate.common.types";
import {
    isProposalGrouped,
    isProposalGroupForDisplay,
} from "entity/estimate/common/FormattedProposal/FormattedProposal.utils";
import { hasNutrientApprovalActions } from "entity/estimate/common/FormattedProposal/FormattedProposalSignatures";
import { type CostCodeExtraData } from "entity/estimate/Estimate/Estimate.api.types";
import {
    ProposalSignatureStatus,
    type ProposalStatusObject,
} from "entity/estimate/Proposal/Proposal.types";

/**
 * `isExpired` only applies when the proposal is still Pending. Once it has been
 * decided (Approved/Declined/etc.), an elapsed approval deadline is irrelevant
 * and we must show the real status.
 */
export function isExpiredWithoutDecision(proposal: {
    status: ProposalStatus;
    isExpired: boolean;
}): boolean {
    return proposal.isExpired && proposal.status === ProposalStatus.Pending;
}

export const getProposalStatusText = (
    status: ProposalStatus,
    isLead?: boolean,
    isExpired?: boolean,
    isExistingProposal?: boolean,
) => {
    if (isExpired) {
        return "Expired";
    }

    switch (status) {
        case ProposalStatus.Unreleased:
            if (isLead) {
                return "Not sent";
            } else {
                return isExistingProposal ? "Recalled" : "New";
            }
        case ProposalStatus.Pending:
            return "Sent";
        case ProposalStatus.Approved:
        case ProposalStatus.ManualApprove:
        case ProposalStatus.ApprovedUnreleased:
            return "Approved";
        case ProposalStatus.ManualDecline:
        case ProposalStatus.Declined:
        case ProposalStatus.DeclinedUnreleased:
            return "Declined";
        case ProposalStatus.Expired:
            return "Expired";
        case ProposalStatus.DraftPendingSignature:
        case ProposalStatus.Draft:
            return "Draft";
    }
};

export const getProposalStatusType = (status: ProposalStatus): StatusTypes => {
    switch (status) {
        case ProposalStatus.DeclinedUnreleased:
        case ProposalStatus.ManualDecline:
        case ProposalStatus.Declined:
            return "danger";
        case ProposalStatus.Approved:
        case ProposalStatus.ManualApprove:
        case ProposalStatus.ApprovedUnreleased:
            return "success";
        case ProposalStatus.DraftPendingSignature:
        case ProposalStatus.Draft:
            return "info";
        case ProposalStatus.Pending:
            return "pending";
        case ProposalStatus.Expired:
        case ProposalStatus.Unreleased:
            return "";
    }
};

export function getBdsBadgeDisplayTypeForProposalStatus(
    status: ProposalStatus,
): TBTStatusBadgeDisplayTypes {
    switch (status) {
        case ProposalStatus.DeclinedUnreleased:
        case ProposalStatus.ManualDecline:
        case ProposalStatus.Declined:
            return "danger";
        case ProposalStatus.Approved:
        case ProposalStatus.ManualApprove:
        case ProposalStatus.ApprovedUnreleased:
            return "success";
        case ProposalStatus.DraftPendingSignature:
        case ProposalStatus.Draft:
            return "info";
        case ProposalStatus.Pending:
            return "warning";
        case ProposalStatus.Expired:
        case ProposalStatus.Unreleased:
            return "default";
    }
}

export const getStatusTextForJobProposal = (
    proposalStatus: ProposalStatusObject,
    timeLocale: TimeLocale,
) => {
    if (
        proposalStatus.status === ProposalStatus.Unreleased ||
        proposalStatus.status === ProposalStatus.Draft
    ) {
        return "";
    }

    if (proposalStatus.date === null || proposalStatus.user === null) {
        return "";
    }

    const expired = isExpiredWithoutDecision(proposalStatus);
    const statusDetailsText = getProposalStatusText(
        expired ? ProposalStatus.Expired : proposalStatus.status,
    );
    const userText = expired ? " " : ` by ${proposalStatus.user} `;
    const dateText =
        "on " +
        formatDate(moment(proposalStatus.date), timeLocale, {
            showDate: true,
            showMonth: true,
            showYear: true,
        });

    const allDetails = ` -> ${statusDetailsText}${userText}${dateText}`;

    return allDetails;
};

export const hasProposalBeenActedOn = (proposal: ProposalForDisplay) => {
    // Check legacy signatures first
    if (proposal.signatures.some((sig) => sig.approvalType !== ProposalSignatureStatus.Pending)) {
        return true;
    }

    // No action has been taken involving legacy signatures, check Nutrient approvals
    return hasNutrientApprovalActions(proposal);
};

export const hasProposalBeenRecalled = (proposalData: ProposalForDisplay) => {
    return proposalData.proposalId > 0 && proposalData.status.status === ProposalStatus.Unreleased;
};

export const getPlaceholderText = (status: ApprovalDetailsForDisplay, timeLocale: TimeLocale) => {
    const approvedStatuses: ProposalStatus[] = [
        ProposalStatus.Approved,
        ProposalStatus.ManualApprove,
        ProposalStatus.ApprovedUnreleased,
    ];
    const statusText = approvedStatuses.includes(status.status) ? "Approved" : "Declined";
    return `${statusText} Proposal ${formatDateAsApFormatWithTime(moment(), timeLocale.format)}`;
};

const getGroupingEntityLanguageConstants = (
    viewType: ProposalGroupTypes,
): ILanguageConstants & { indefiniteArticle: "a" | "an" } => {
    switch (viewType) {
        case ProposalGroupTypes.None:
        case ProposalGroupTypes.Assembly:
        case ProposalGroupTypes.ProposalWorksheet:
            return { singular: "Group", plural: "Groups", indefiniteArticle: "a" };
        case ProposalGroupTypes.CostCategory:
            return { singular: "Category", plural: "Categories", indefiniteArticle: "a" };
        case ProposalGroupTypes.Takeoff:
            return { singular: "Assembly", plural: "Assemblies", indefiniteArticle: "an" };
    }
};

export interface IProposalGroupingEntityNameConfig {
    isSubEntity?: boolean;
    isPlural?: boolean;
    addIndefiniteArticle?: boolean;
}

export const getProposalGroupingEntityName = (
    viewType: ProposalGroupTypes,
    isConnectedToTakeoffs: boolean,
    isLeadProposal: boolean,
    config?: IProposalGroupingEntityNameConfig,
) => {
    // Lead proposals should use takeoffs language for its custom view/grouping when connected to takeoffs
    const viewTypeForVerbiage =
        isLeadProposal && viewType === ProposalGroupTypes.Assembly && isConnectedToTakeoffs
            ? ProposalGroupTypes.Takeoff
            : viewType;

    const baseEntityNameOptions = getGroupingEntityLanguageConstants(viewTypeForVerbiage);
    const baseEntityName = config?.isPlural
        ? baseEntityNameOptions.plural
        : baseEntityNameOptions.singular;

    const entityName = config?.isSubEntity ? `Sub${baseEntityName.toLowerCase()}` : baseEntityName;

    if (config?.addIndefiniteArticle && !config?.isPlural) {
        return `${baseEntityNameOptions.indefiniteArticle} ${entityName}`;
    }
    return entityName;
};

export const getProposalViewName = (
    viewType: ProposalGroupTypes,
    isProjectConnectedToTakeoffs: boolean,
): string => {
    switch (viewType) {
        case ProposalGroupTypes.None:
            return "None";
        case ProposalGroupTypes.CostCategory:
            return "Cost Category";
        case ProposalGroupTypes.Assembly:
            // As a (hopefully) short term quick-win, we will rename "Assemblies" to "Groups" or "Custom"
            // for builders not connected to Takeoffs. In the database anything stored in related modals
            // will still save in the dimension.assemblies table
            return isProjectConnectedToTakeoffs ? "Assembly" : "Custom";
        case ProposalGroupTypes.ProposalWorksheet:
            return "Proposal Worksheet";
        case ProposalGroupTypes.Takeoff:
            return "Takeoff Assemblies";
    }
};

export const projectConnectedToTakeoffs = (takeoffProjectLink: string | null | undefined) => {
    return (
        takeoffProjectLink !== null && takeoffProjectLink !== undefined && takeoffProjectLink !== ""
    );
};

export const getLeadProposalAssemblyTitle = (projectConnectedToTakeoffs: boolean) => {
    return getProposalGroupingEntityName(
        ProposalGroupTypes.Assembly,
        projectConnectedToTakeoffs,
        true,
    );
};

const getFilesToDisplayForPrint = (showAllImages: boolean, files: readonly BTFileSystem[]) => {
    const imageFiles = files.filter((file) => file.mediaType === MediaTypes.Photo);
    return showAllImages ? imageFiles : [...imageFiles].splice(0, 4);
};

const getFormatItemsForPrint = (
    showAllImages: boolean,
    formatItems: readonly ProposalForDisplayFormatItemsItem[],
) => {
    // we only need to adjust files, so we can skip processing if there are no groups
    if (!isProposalGrouped(formatItems)) {
        return formatItems;
    }

    return formatItems.map((group) => {
        const newItems = group.items.map((item) => {
            if (isProposalGroupForDisplay(item) && item.files.length > 0) {
                return { ...item, files: getFilesToDisplayForPrint(showAllImages, item.files) };
            } else {
                return item;
            }
        });

        return {
            ...group,
            files: getFilesToDisplayForPrint(showAllImages, group.files),
            items: newItems,
        };
    });
};

export const getProposalForDisplayForPrint = (
    originalProposalForDisplay: ProposalForDisplay,
    printOptions: ProposalPrintOptionsDto,
): ProposalForDisplay => {
    return {
        ...originalProposalForDisplay,
        // we don't want the interactive file viewer on the print page
        attachedFilesBase: null,
        files: getFilesToDisplayForPrint(
            printOptions.showAllImages,
            originalProposalForDisplay.files,
        ),
        signatureRequirement: printOptions.hideSignatures
            ? ProposalSignatureRequirement.None
            : originalProposalForDisplay.signatureRequirement,
        signatures: printOptions.hideSignatures ? [] : originalProposalForDisplay.signatures,
        formatItems: getFormatItemsForPrint(
            printOptions.showAllImages,
            originalProposalForDisplay.formatItems,
        ),
    };
};

/**
 * Calculates the number of signature lines to display on a proposal.
 * Falls back to clientUserCountOnJob if no signatures are found.
 */
export const getNumberOfSignatureLines = (
    proposalData: ProposalForDisplay,
    draftSignatureUsersCount: number,
    clientUserCountOnJob: number,
): number => {
    let selectedCount = 0;
    // For draft proposals, count selected signature users
    if (proposalData.proposalId) {
        selectedCount = proposalData.userApprovals?.length ?? 0;
    } else {
        selectedCount = draftSignatureUsersCount;
    }
    if (selectedCount === 0) {
        selectedCount = clientUserCountOnJob;
    }
    return selectedCount;
};

const buildIntroductionTextWithCoId = (
    introductionText: string | undefined,
    coIdPrefix: string | undefined,
    changeOrderId: string | undefined,
): string => {
    const parts: string[] = [];
    if (coIdPrefix !== undefined && changeOrderId !== undefined) {
        parts.push(`Change Order ID: <strong>${coIdPrefix}${changeOrderId}</strong>`);
    }
    if (introductionText) {
        parts.push(introductionText);
    }
    return parts.join("<br/><br/>");
};

const buildClosingTextWithCustomFields = (
    closingText: string | undefined,
    customFieldFormValues: ICustomFieldFormValues[],
    customFieldDefinitions: ICustomFieldResponse[],
    customFieldOptions: ICustomFieldOptionsList,
    builderInfo: BuilderInfo,
    formatDate: (value: Date, options: IDateDisplayFormattingProps) => string,
): string => {
    const ownerVisibleFields = customFieldDefinitions.filter((cf) => cf.onOwnerPortal);
    const customFieldStrings = ownerVisibleFields
        .map((fieldDef) => {
            const formValue = customFieldFormValues.find((fv) => fv.id === fieldDef.id);
            if (!formValue || formValue.value === null || formValue.value === "") {
                return null;
            }
            const effectiveValue = normalizeCustomFieldFormValueForDisplay(
                formValue.value,
                fieldDef.typeId,
            );
            const displayValue = formatCustomFieldToString(
                effectiveValue,
                fieldDef.typeId,
                mapResponseToCustomFieldDefinition(fieldDef, customFieldOptions),
                builderInfo?.locale.currencyLocale ?? CurrencyLocale.default,
                formatDate,
            );
            return `&bull; ${fieldDef.title}: ${displayValue}`;
        })
        .filter((s): s is string => s !== null);

    const customFieldsString = customFieldStrings.join("<br />");

    const parts: string[] = [];
    if (closingText) {
        parts.push(closingText);
    }
    if (customFieldsString) {
        parts.push(customFieldsString);
    }
    return parts.join("<br/><br/>");
};

const getCostCodeTitle = (
    lineItem: WorksheetLineItem,
    costCodes?: BTSelectItem<CostCodeExtraData>[],
): string => {
    if (lineItem.costCodeTitle) {
        return lineItem.costCodeTitle;
    }

    // Need to remove sub-code intendation ellipses and spaces if they are present.
    let costCodeTitle = costCodes?.find((cc) => cc.value === lineItem.costCodeId)?.title ?? "";
    while (costCodeTitle.startsWith("...")) {
        costCodeTitle = costCodeTitle.substring(3);
    }

    return costCodeTitle.trimStart();
};

const mapWorksheetLineItemToProposalLineItem = (
    lineItem: WorksheetLineItem,
    costCodes?: BTSelectItem<CostCodeExtraData>[],
): ProposalLineItemForDisplay => {
    return {
        type: "lineItem",
        proposalFormatItemId: lineItem.formatId ?? lineItem.id,
        parentId: lineItem.parentId ?? null,
        displayOrder: lineItem.displayOrder ?? 0,
        title: lineItem.itemTitle ?? "",
        costCodeTitle: getCostCodeTitle(lineItem, costCodes),
        description: lineItem.description ?? "",
        costTypes: lineItem.costTypes ?? [],
        markedAs: lineItem.markedAs,
        unitCost: lineItem.unitCost,
        unitPrice: lineItem.quantity ? lineItem.ownerPrice / lineItem.quantity : 0,
        unit: lineItem.unit,
        quantity: lineItem.quantity,
        builderCost: lineItem.builderCost,
        markupType: lineItem.markupType,
        markupPercent: lineItem.markupPercent,
        markupPerUnit: lineItem.markupPerUnit,
        markupAmount: lineItem.markupAmount,
        ownerPrice: lineItem.ownerPrice,
        taxGroupId: lineItem.taxGroupId ?? null,
    };
};

export const applyFormValuesToProposalForDisplay = (
    base: ProposalForDisplay,
    formValues: IChangeOrderFormValues,
    worksheetFormValues: IChangeOrderWorksheetFormValues,
    changeOrder: ChangeOrderEntity,
    builderInfo: BuilderInfo,
    formatDate: (value: Date, options: IDateDisplayFormattingProps) => string,
    costCodes?: BTSelectItem<CostCodeExtraData>[],
): ProposalForDisplay => {
    const allLineItems = getFormatLineItemsFromFormatItemList(worksheetFormValues.formatItems);

    const baseBuilderCost = allLineItems.reduce((sum, li) => sum + li.builderCost, 0);
    const baseMarkupAmount = allLineItems.reduce((sum, li) => sum + li.markupAmount, 0);
    const baseOwnerPrice = allLineItems.reduce((sum, li) => sum + li.ownerPrice, 0);
    // For non-taxable lines, frontend recalc still applies the job's effective
    // rate to totalWithTax — use ownerPrice instead. For taxable lines, fall back
    // to ownerPrice when totalWithTax is missing/zero (historical data saved
    // before the CO tax feature could leave totalWithTax unpopulated).
    const baseTotalPriceWithTax = allLineItems.reduce((sum, li) => {
        const lineTotal = isTaxGroupIdValid(li.taxGroupId)
            ? li.totalWithTax || li.ownerPrice
            : li.ownerPrice;
        return sum + lineTotal;
    }, 0);
    const baseTotalTax = baseTotalPriceWithTax - baseOwnerPrice;

    return {
        ...base,
        title: formValues.title,
        approvalDeadline: formValues.deadLine ?? null,
        introductionText: buildIntroductionTextWithCoId(
            formValues.introductionText,
            changeOrder.customChangeOrderId.prefix,
            changeOrder.customChangeOrderId.value,
        ),
        closingText: buildClosingTextWithCustomFields(
            formValues.closingText,
            formValues.customFields,
            changeOrder.customFields ?? [],
            changeOrder.customFieldOptions,
            builderInfo,
            formatDate,
        ),
        whatToDisplay: formValues.displayFormatOptions.selectedItems as ProposalColumnTypes[],
        formatItems: allLineItems.map((li) =>
            mapWorksheetLineItemToProposalLineItem(li, costCodes),
        ),
        baseBuilderCost,
        baseMarkupAmount,
        baseOwnerPrice,
        baseSubtotal: baseOwnerPrice,
        baseTotalTax,
        baseTotalPriceWithTax,
        signatureRequirement:
            formValues.requiredApprovers.length > 0
                ? ProposalSignatureRequirement.Some
                : ProposalSignatureRequirement.None,
        signatures: base?.signatures?.filter((s) =>
            formValues.requiredApprovers.includes(s.proposalRequestOrgLinkId ?? 0),
        ),
    };
};

const addressFieldNameToType: Record<string, AddressFieldTypes> = {
    street: AddressFieldTypes.Street,
    suburb: AddressFieldTypes.Suburb,
    city: AddressFieldTypes.City,
    state: AddressFieldTypes.State,
    zip: AddressFieldTypes.Zip,
    country: AddressFieldTypes.Country,
};

export function getFieldFromAddressEntity(
    address: ProposalForDisplayCustomerAddress,
    fieldName: string,
) {
    const addressFieldType = addressFieldNameToType[fieldName];
    const fieldValue = address?.fields.find((x) => x.addressFieldType === addressFieldType);
    return fieldValue?.value ?? null;
}
